/* Work with Kaia Relief: partnership pages.
   One file for the route cards, the affiliate/business switch, the earnings
   calculator and the application forms. No dependencies. Loaded with defer.

   Privacy rules this file keeps:
   - Analytics events carry only route, category, step and an error type.
     Never a name, email, free-text answer or license number.
   - Nothing an applicant types goes into a URL. Applications are POSTed as JSON.
   - Session storage is used only to carry name, email, state and organization
     when an applicant is sent to a different application, and is cleared on read. */
(function () {
  if (window.__wwkLoaded) return;
  window.__wwkLoaded = true;

  var HANDLES = ['work-with-kaia', 'practitioner-partnerships', 'creator-partnerships', 'affiliates-partnerships'];
  var HANDOFF_KEY = 'wwk_handoff';

  /* ------------------------------------------------------------ analytics */
  function track(name, props) {
    var data = props || {};
    try {
      window.dataLayer = window.dataLayer || [];
      var e = { event: name };
      for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) e['wwk_' + k] = data[k];
      window.dataLayer.push(e);
    } catch (err) {}
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data);
      }
    } catch (err) {}
  }

  /* ------------------------------------------------------------ preview links
     The four pages stay hidden until launch, and Shopify returns 404 for a
     hidden page even in theme preview. The review previews therefore load each
     template onto an existing page with ?view=. On those previews only, links
     between the four pages are rewritten to the matching ?view= URL so the
     reviewer can click through. On the real pages (no ?view=) nothing changes. */
  function rewritePreviewLinks() {
    var params = new URLSearchParams(location.search);
    if (!params.get('view')) return;
    var here = location.pathname.replace(/\/$/, '');
    var hereHandle = here.split('/').pop();
    if (HANDLES.indexOf(hereHandle) !== -1) return;
    document.querySelectorAll('a[href]').forEach(function (a) {
      var url;
      try { url = new URL(a.getAttribute('href'), location.origin); } catch (e) { return; }
      if (url.origin !== location.origin) return;
      var m = url.pathname.match(/^\/pages\/([a-z0-9-]+)\/?$/);
      if (!m || HANDLES.indexOf(m[1]) === -1) return;
      var q = new URLSearchParams(url.search);
      q.set('view', m[1]);
      a.setAttribute('href', here + '?' + q.toString() + url.hash);
    });
  }

  /* ------------------------------------------------------------ route cards */
  function initRoutes() {
    document.querySelectorAll('[data-wwk-route]').forEach(function (a) {
      a.addEventListener('click', function () {
        track('wwk_route_select', { route: a.getAttribute('data-wwk-route') });
      });
    });
  }

  /* ------------------------------------------------------------ path switch */
  function initPaths() {
    var sw = document.querySelector('[data-wwk-paths]');
    if (!sw) return;
    var buttons = sw.querySelectorAll('[data-wwk-path-button]');
    var panels = document.querySelectorAll('[data-wwk-path-panel]');
    var editor = sw.hasAttribute('data-wwk-design-mode');
    if (editor) return; // the theme editor shows every panel so each can be edited

    function activate(path, fromUser) {
      var found = false;
      buttons.forEach(function (b) {
        var on = b.getAttribute('data-wwk-path-button') === path;
        if (on) found = true;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (!found) return false;
      panels.forEach(function (p) {
        var v = p.getAttribute('data-wwk-path-panel');
        p.hidden = !(v === 'all' || v === path);
      });
      if (fromUser) {
        track('wwk_path_select', { route: path });
        try { history.replaceState(null, '', location.pathname + location.search + '#' + path); } catch (e) {}
      }
      return true;
    }

    buttons.forEach(function (b) {
      b.addEventListener('click', function () { activate(b.getAttribute('data-wwk-path-button'), true); });
    });

    function fromLocation() {
      var hash = (location.hash || '').slice(1);
      var q = new URLSearchParams(location.search).get('path');
      if (hash && activate(hash, false)) return;
      if (hash) {
        var target = document.getElementById(hash);
        var panel = target && target.closest('[data-wwk-path-panel]');
        if (panel && activate(panel.getAttribute('data-wwk-path-panel'), false)) {
          scrollToTarget(target);
          return;
        }
      }
      if (q && activate(q, false)) return;
      activate(sw.getAttribute('data-wwk-default-path') || 'affiliate', false);
    }
    fromLocation();
    window.addEventListener('hashchange', fromLocation);
  }

  /* ------------------------------------------------------------ calculator */
  var usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

  function initCalculators() {
    document.querySelectorAll('[data-wwk-calc]:not([data-wwk-ready])').forEach(function (root) {
      root.setAttribute('data-wwk-ready', '');
      var rate = parseFloat(root.getAttribute('data-rate'));
      var hasRate = isFinite(rate) && rate > 0;
      var pairs = [['orders', 0], ['aov', 2]];
      var engaged = false;
      var out = {
        rate: root.querySelector('[data-out="rate"]'),
        per: root.querySelector('[data-out="per-order"]'),
        month: root.querySelector('[data-out="monthly"]'),
        year: root.querySelector('[data-out="annual"]'),
        yearRow: root.querySelector('[data-annual-row]')
      };
      var annualToggle = root.querySelector('[data-annual-toggle]');

      function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }
      function read(name) {
        var n = root.querySelector('[data-num="' + name + '"]');
        var v = parseFloat(n.value);
        return isFinite(v) ? v : 0;
      }
      function sync(name, source) {
        var r = root.querySelector('[data-range="' + name + '"]');
        var n = root.querySelector('[data-num="' + name + '"]');
        var min = parseFloat(n.min), max = parseFloat(n.max);
        if (source === 'range') n.value = r.value;
        else {
          var v = parseFloat(n.value);
          if (isFinite(v)) r.value = clamp(v, parseFloat(r.min), parseFloat(r.max));
        }
        var v2 = parseFloat(n.value);
        n.setAttribute('aria-invalid', (!isFinite(v2) || v2 < min || v2 > max) ? 'true' : 'false');
      }
      function render() {
        var orders = Math.max(0, Math.round(read('orders')));
        var aov = Math.max(0, read('aov'));
        if (!hasRate) {
          out.per.textContent = '–'; out.month.textContent = '–'; if (out.year) out.year.textContent = '–';
          return;
        }
        // Qualifying orders x average commissionable order value x commission rate.
        // The order value is already after discounts, so no discount is taken off here.
        var perOrder = Math.round(aov * rate) / 100;
        var monthlyCents = Math.round(orders * aov * rate); // cents, since rate is a percentage
        out.per.textContent = usd.format(perOrder);
        out.month.textContent = usd.format(monthlyCents / 100);
        if (out.year) out.year.textContent = usd.format((monthlyCents * 12) / 100);
      }
      function engage() {
        if (engaged) return;
        engaged = true;
        track('wwk_calculator_engage', {});
      }
      pairs.forEach(function (p) {
        var name = p[0];
        var r = root.querySelector('[data-range="' + name + '"]');
        var n = root.querySelector('[data-num="' + name + '"]');
        r.addEventListener('input', function () { sync(name, 'range'); render(); engage(); });
        n.addEventListener('input', function () { sync(name, 'num'); render(); engage(); });
        n.addEventListener('blur', function () {
          var v = parseFloat(n.value), min = parseFloat(n.min), max = parseFloat(n.max);
          if (!isFinite(v)) v = parseFloat(r.value);
          v = clamp(v, min, max);
          n.value = p[1] ? (Math.round(v * 100) / 100) : Math.round(v);
          sync(name, 'num'); render();
        });
      });
      if (annualToggle && out.yearRow) {
        annualToggle.addEventListener('change', function () {
          out.yearRow.hidden = !annualToggle.checked; engage();
        });
      }
      render();
    });
  }

  /* ------------------------------------------------------------ forms */
  var MESSAGES = {
    required: 'This is required.',
    email: 'Enter an email address, like name@example.com.',
    link: 'Enter a link, like https://example.com, or a handle, like @name.',
    npi: 'An NPI is 10 digits.',
    choose: 'Choose at least one option.',
    ack: 'Please confirm to submit your application.',
    date: 'Enter a date.',
    too_long: 'This is too long. Please shorten it.',
    server: 'Please check this answer.'
  };

  function initForms() {
    document.querySelectorAll('[data-wwk-form]:not([data-wwk-ready])').forEach(function (f) {
      f.setAttribute('data-wwk-ready', '');
      initForm(f);
    });
  }

  function initForm(form) {
    if (form.hasAttribute('data-wwk-preview')) return; // theme editor state preview
    var root = form.closest('[data-wwk-app]');
    var category = form.getAttribute('data-category');
    var endpoint = form.getAttribute('data-endpoint');
    var steps = Array.prototype.slice.call(form.querySelectorAll('[data-wwk-step]'));
    var progress = root.querySelectorAll('[data-wwk-progress] li');
    var status = root.querySelector('[data-wwk-status]');
    var summary = form.querySelector('[data-wwk-summary]');
    var submitBtn = form.querySelector('[data-wwk-submit]');
    var current = 0;
    var started = false;
    var startedAt = Date.now();
    var idem = uuid();
    var busy = false;
    var utm = captureUtm();

    form.hidden = false;
    var ns = root.querySelector('[data-wwk-noscript]');
    if (ns) ns.hidden = true;
    root.querySelectorAll('[data-wwk-progress]').forEach(function (p) { p.hidden = false; });

    // Prefill from a hand-off, then clear it.
    applyHandoff(form, category, root);

    // Conditional fields.
    form.addEventListener('change', function () { applyConditions(form); });
    form.addEventListener('input', function (e) {
      if (!started) { started = true; track('wwk_application_start', { category: category }); }
      var f = e.target.closest('.wwk-field');
      if (f && f.classList.contains('has-error')) clearFieldError(f);
    });
    applyConditions(form);

    // Step navigation.
    form.querySelectorAll('[data-wwk-next]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (validateStep(steps[current])) go(current + 1);
      });
    });
    form.querySelectorAll('[data-wwk-back]').forEach(function (b) {
      b.addEventListener('click', function () { go(current - 1); });
    });

    // Hand-off buttons.
    root.querySelectorAll('[data-wwk-handoff]').forEach(function (a) {
      a.addEventListener('click', function () {
        var to = a.getAttribute('data-wwk-handoff');
        try {
          sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({
            to: to, from: category, at: Date.now(),
            full_name: raw(form, 'full_name'), email: raw(form, 'email'),
            us_state: raw(form, 'us_state'), organization: raw(form, 'organization')
          }));
        } catch (e) {}
        track('wwk_route_handoff', { category: category, route: to });
      });
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;
      if (current !== steps.length - 1) {
        if (validateStep(steps[current])) go(current + 1);
        return;
      }
      if (!validateStep(steps[current])) return;
      send();
    });

    root.querySelectorAll('[data-wwk-retry]').forEach(function (b) {
      b.addEventListener('click', function () {
        showResult(null);
        form.hidden = false;
        go(steps.length - 1, true);
        if (submitBtn) submitBtn.focus();
      });
    });

    go(0, false, true);

    function go(i, force, initial) {
      if (i < 0 || i >= steps.length) return;
      steps.forEach(function (s, idx) { s.hidden = idx !== i; });
      progress.forEach(function (li, idx) {
        if (idx === i) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
        li.classList.toggle('is-done', idx < i);
      });
      current = i;
      hideSummary();
      if (!initial) {
        var h = steps[i].querySelector('.wwk-step__title');
        if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
        var panel = root.querySelector('.wwk-app__panel') || root;
        var top = panel.getBoundingClientRect().top + window.pageYOffset - headerOffset();
        if (Math.abs(window.pageYOffset - top) > 40) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        track('wwk_application_step', { category: category, step: i + 1 });
      }
    }

    function send() {
      busy = true;
      setBusy(true);
      var payload;
      try { payload = buildPayload(form, category, idem, startedAt, utm); }
      catch (e) { busy = false; setBusy(false); return; }
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        cache: 'no-store'
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) { return { code: r.status, body: body }; });
      }).then(function (res) {
        busy = false; setBusy(false);
        var b = res.body || {};
        if ((res.code === 201 || res.code === 200) && b.status === 'received' && b.ref) {
          form.hidden = true;
          showResult('success', b.ref);
          track('wwk_application_submit_success', { category: category });
          return;
        }
        if (res.code === 409 && b.error === 'duplicate_recent') {
          form.hidden = true;
          showResult('duplicate');
          track('wwk_application_submit_error', { category: category, error_type: 'duplicate' });
          return;
        }
        if (res.code === 400 && b.error === 'invalid' && b.field) {
          track('wwk_application_submit_error', { category: category, error_type: 'validation' });
          if (b.reason === 'route_mismatch') { go(0); return; }
          if (!markServerError(b.field, b.reason)) showResult('error');
          return;
        }
        var type = res.code === 429 ? 'rate_limited' : (res.code >= 500 ? 'server' : 'rejected');
        track('wwk_application_submit_error', { category: category, error_type: type });
        form.hidden = true;
        showResult(res.code === 429 ? 'rate' : 'error');
      }).catch(function () {
        busy = false; setBusy(false);
        track('wwk_application_submit_error', { category: category, error_type: 'network' });
        form.hidden = true;
        showResult('error');
      });
    }

    function markServerError(field, reason) {
      var el = form.querySelector('[name="' + field + '"]:not([disabled])') || form.querySelector('[data-wwk-group="' + field + '"]');
      if (!el) return false;
      var step = el.closest('[data-wwk-step]');
      var idx = steps.indexOf(step);
      if (idx === -1) return false;
      go(idx);
      var f = el.closest('.wwk-field');
      setFieldError(f, MESSAGES[reason] || MESSAGES.server);
      showSummary(steps[idx], [f]);
      return true;
    }

    function setBusy(on) {
      if (!submitBtn) return;
      submitBtn.disabled = on;
      submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
      var label = submitBtn.querySelector('[data-label]');
      if (label) label.textContent = on ? submitBtn.getAttribute('data-busy-label') : submitBtn.getAttribute('data-idle-label');
      if (status) status.textContent = on ? submitBtn.getAttribute('data-busy-label') : '';
    }

    function showResult(kind, ref) {
      // Hide the progress bar first, so the scroll below is measured on the final layout.
      root.querySelectorAll('[data-wwk-progress]').forEach(function (p) { p.hidden = kind === 'success' || kind === 'duplicate'; });
      root.querySelectorAll('[data-wwk-result]').forEach(function (el) {
        el.hidden = el.getAttribute('data-wwk-result') !== kind;
        if (!el.hidden) {
          var r = el.querySelector('[data-wwk-ref]');
          if (r && ref) r.textContent = ref;
          el.setAttribute('tabindex', '-1');
          el.focus({ preventScroll: true });
          var top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 16;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      });
    }

    function validateStep(step) {
      var bad = [];
      step.querySelectorAll('.wwk-field').forEach(function (f) {
        var hiddenBy = f.closest('[hidden]');
        if (hiddenBy && step.contains(hiddenBy)) { clearFieldError(f); return; }
        var msg = checkField(f);
        if (msg) { setFieldError(f, msg); bad.push(f); } else clearFieldError(f);
      });
      if (bad.length) {
        showSummary(step, bad);
        track('wwk_application_validation', { category: category, step: current + 1 });
        return false;
      }
      hideSummary();
      return true;
    }

    function showSummary(step, bad) {
      if (!summary) return;
      var ul = summary.querySelector('ul');
      ul.innerHTML = '';
      bad.forEach(function (f) {
        var id = f.getAttribute('data-first-id');
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + id;
        a.textContent = f.getAttribute('data-label') || 'This field';
        a.addEventListener('click', function (e) {
          e.preventDefault();
          var el = document.getElementById(id);
          if (el) el.focus();
        });
        li.appendChild(a);
        ul.appendChild(li);
      });
      summary.hidden = false;
      summary.focus({ preventScroll: true });
      var st = summary.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 16;
      window.scrollTo({ top: Math.max(0, st), behavior: 'smooth' });
    }
    function hideSummary() { if (summary) summary.hidden = true; }
  }

  /* Scroll a hash target below the theme header. Runs again after the page has
     fully loaded, because late layout (images, the path panels) can otherwise
     leave the visitor at the top of the page. */
  function scrollToTarget(target) {
    function go() {
      var y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
      window.scrollTo(0, Math.max(0, y));
    }
    setTimeout(go, 60);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { setTimeout(go, 60); }, { once: true });
    }
  }

  function headerOffset() {
    var h = document.querySelector('header-component') || document.querySelector('header');
    var b = h ? h.getBoundingClientRect().bottom : 0;
    return (b > 0 && b < 200 ? b : 80) + 12;
  }

  /* On phones the theme header is drawn over the top of the page. When a WWK
     hero is the first thing under it, pad the hero by exactly the overlap so the
     heading is never hidden. Nothing happens where the header sits in the flow. */
  function headerClearance() {
    var hero = document.querySelector('.wwk-hero');
    var hc = document.querySelector('header-component');
    if (!hero || !hc) return;
    hero.style.paddingTop = '';
    var pos = getComputedStyle(hc).position;
    if (pos !== 'absolute' && pos !== 'fixed') return;
    var overlap = (hc.getBoundingClientRect().bottom + window.pageYOffset) - (hero.getBoundingClientRect().top + window.pageYOffset);
    if (overlap > 0 && overlap < 240) hero.style.paddingTop = Math.ceil(overlap) + 'px';
  }

  function checkField(f) {
    var group = f.querySelector('[data-wwk-group]');
    if (group) {
      var boxes = group.querySelectorAll('input:not([disabled])');
      var any = Array.prototype.some.call(boxes, function (b) { return b.checked; });
      if (group.hasAttribute('data-required') && !any) return group.querySelector('input[type="radio"]') ? MESSAGES.required : MESSAGES.choose;
      return '';
    }
    var el = f.querySelector('input, select, textarea');
    if (!el || el.disabled) return '';
    if (el.type === 'checkbox') {
      if (el.required && !el.checked) return el.hasAttribute('data-ack') ? MESSAGES.ack : MESSAGES.required;
      return '';
    }
    var v = (el.value || '').trim();
    if (!v) return el.required ? MESSAGES.required : '';
    var max = parseInt(el.getAttribute('maxlength') || '0', 10);
    if (max && v.length > max) return MESSAGES.too_long;
    var kind = el.getAttribute('data-kind') || el.type;
    if (kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return MESSAGES.email;
    if (kind === 'link' && !/^(https?:\/\/)?[^\s]+\.[^\s]{2,}$|^@[A-Za-z0-9._]{2,}$/.test(v)) return MESSAGES.link;
    if (kind === 'npi' && !/^\d{10}$/.test(v)) return MESSAGES.npi;
    if (kind === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(v)) return MESSAGES.date;
    return '';
  }

  function setFieldError(f, msg) {
    if (!f) return;
    f.classList.add('has-error');
    var err = f.querySelector('.wwk-error');
    if (err) { err.textContent = msg; err.hidden = false; }
    f.querySelectorAll('input, select, textarea').forEach(function (el) {
      if (el.type === 'checkbox' || el.type === 'radio') return;
      el.setAttribute('aria-invalid', 'true');
    });
    var group = f.querySelector('[data-wwk-group]');
    if (group) group.setAttribute('aria-invalid', 'true');
    var single = f.querySelector('input[type="checkbox"][required]');
    if (single) single.setAttribute('aria-invalid', 'true');
  }
  function clearFieldError(f) {
    f.classList.remove('has-error');
    var err = f.querySelector('.wwk-error');
    if (err) { err.hidden = true; err.textContent = ''; }
    f.querySelectorAll('[aria-invalid]').forEach(function (el) { el.removeAttribute('aria-invalid'); });
  }

  /* data-wwk-show-if="name=a|b" shows a block when that field has one of those
     values (any checked value, for checkbox groups). data-wwk-hide-if is the
     reverse. data-wwk-required-if on a control makes it required under the same
     kind of rule. Hidden blocks have their controls disabled, so they are
     neither validated nor sent. */
  function values(form, name) {
    var els = form.querySelectorAll('[name="' + name + '"]');
    var out = [];
    els.forEach(function (el) {
      if (el.disabled) return;
      if (el.type === 'checkbox' || el.type === 'radio') { if (el.checked) out.push(el.value); }
      else if (el.value) out.push(el.value);
    });
    return out;
  }
  function matches(form, rule) {
    return rule.split('&').every(function (part) {
      var neg = part.indexOf('!=') !== -1;
      var bits = part.split(neg ? '!=' : '=');
      var have = values(form, bits[0].trim());
      var want = (bits[1] || '').split('|').map(function (s) { return s.trim(); });
      var hit = want.some(function (w) { return w === '*' ? have.length > 0 : have.indexOf(w) !== -1; });
      return neg ? !hit : hit;
    });
  }
  function applyConditions(form) {
    for (var pass = 0; pass < 3; pass++) {
      form.querySelectorAll('[data-wwk-show-if]').forEach(function (el) {
        var show = matches(form, el.getAttribute('data-wwk-show-if'));
        el.hidden = !show;
        el.querySelectorAll('input, select, textarea').forEach(function (c) {
          var hiddenAncestor = c.closest('[data-wwk-show-if][hidden]');
          c.disabled = !!hiddenAncestor;
        });
      });
    }
    form.querySelectorAll('[data-wwk-required-if]').forEach(function (el) {
      var on = matches(form, el.getAttribute('data-wwk-required-if'));
      if (el.hasAttribute('data-wwk-group')) {
        if (on) el.setAttribute('data-required', ''); else el.removeAttribute('data-required');
      } else {
        el.required = on;
      }
      var f = el.closest('.wwk-field');
      if (f) {
        var req = f.querySelector('[data-req-mark]');
        var opt = f.querySelector('[data-opt-mark]');
        if (req) req.hidden = !on;
        if (opt) opt.hidden = on;
      }
    });
    form.querySelectorAll('[data-wwk-label-by]').forEach(function (el) {
      var src = el.getAttribute('data-wwk-label-by');
      var v = values(form, src)[0] || 'default';
      var map = {};
      try { map = JSON.parse(el.getAttribute('data-labels')); } catch (e) {}
      el.textContent = map[v] || map['default'] || el.textContent;
    });
  }

  function val(form, name) {
    var el = form.querySelector('[name="' + name + '"]:not([disabled])');
    return el && !el.disabled ? (el.value || '').trim() : '';
  }

  // Hand-offs carry what was typed even if the field is now hidden by the route change.
  function raw(form, name) {
    var els = form.querySelectorAll('[name="' + name + '"]');
    for (var i = 0; i < els.length; i++) { if ((els[i].value || '').trim()) return els[i].value.trim(); }
    return '';
  }

  function buildPayload(form, category, idem, startedAt, utm) {
    var top = ['full_name', 'email', 'us_state', 'organization'];
    var p = {
      category: category,
      answers: {},
      idempotency_key: idem,
      hp: val(form, 'hp_website'),
      consent: { version: form.getAttribute('data-consent-version'), acknowledged: !!(form.querySelector('[name="acknowledgment"]') || {}).checked },
      meta: {
        started_at: startedAt,
        source_path: location.pathname,
        source_template: form.getAttribute('data-template') || '',
        form_version: form.getAttribute('data-form-version'),
        referrer_host: referrerHost(),
        utm: utm
      }
    };
    var seen = {};
    form.querySelectorAll('input[name], select[name], textarea[name]').forEach(function (el) {
      var n = el.name;
      if (el.disabled || n === 'acknowledgment' || n === 'hp_website') return;
      if (seen[n]) return;
      var target = top.indexOf(n) !== -1 ? p : p.answers;
      var els = form.querySelectorAll('[name="' + n + '"]');
      if (el.type === 'checkbox' && els.length > 1) {
        seen[n] = 1;
        target[n] = values(form, n);
      } else if (el.type === 'checkbox') {
        seen[n] = 1;
        target[n] = el.checked;
      } else if (el.type === 'radio') {
        seen[n] = 1;
        target[n] = values(form, n)[0] || '';
      } else {
        seen[n] = 1;
        target[n] = (el.value || '').trim();
      }
    });
    return p;
  }

  function referrerHost() {
    try { return document.referrer ? new URL(document.referrer).host : ''; } catch (e) { return ''; }
  }

  function captureUtm() {
    var out = {};
    try {
      var q = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = q.get(k); if (v) out[k] = v.slice(0, 120);
      });
    } catch (e) {}
    return out;
  }

  function applyHandoff(form, category, root) {
    var data = null;
    try { data = JSON.parse(sessionStorage.getItem(HANDOFF_KEY) || 'null'); } catch (e) {}
    if (!data || data.to !== category || Date.now() - (data.at || 0) > 3600000) return;
    try { sessionStorage.removeItem(HANDOFF_KEY); } catch (e) {}
    var filled = false;
    ['full_name', 'email', 'us_state', 'organization'].forEach(function (n) {
      var el = form.querySelector('[name="' + n + '"]');
      if (el && !el.value && data[n]) { el.value = data[n]; filled = true; }
    });
    var note = root.querySelector('[data-wwk-handoff-note]');
    if (filled && note) note.hidden = false;
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var b = new Uint8Array(16); (window.crypto || window.msCrypto).getRandomValues(b);
    b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
    var h = Array.prototype.map.call(b, function (x) { return ('0' + x.toString(16)).slice(-2); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  function init() {
    headerClearance();
    var rt;
    window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(headerClearance, 150); });
    rewritePreviewLinks();
    initRoutes();
    initPaths();
    if (!document.querySelector('[data-wwk-paths]') && location.hash.length > 1) {
      var hashTarget = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (hashTarget && hashTarget.closest('.wwk')) scrollToTarget(hashTarget);
    }
    initCalculators();
    initForms();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function () {
    initCalculators();
    initForms();
  });
})();
